import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { ListChecks, Pencil, Check, Plus, Trash2, Variable, Brain } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const entities = data.entities || [];

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EDE9FE", padding: "10px", minWidth: "260px", maxWidth: "320px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit entity extractor"
      >
        <Pencil size={12} className="text-indigo-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-indigo-100 rounded-lg">
            <ListChecks size={14} className="text-indigo-600" />
          </div>
          <span className="text-[12px] font-bold text-indigo-900">AI Entity Extractor</span>
        </div>

        {entities.length > 0 ? (
          <div className="space-y-1">
            {entities.map((ent) => (
              <div key={ent.id} className="flex items-center gap-1.5">
                <Variable size={9} className="text-[#008069] flex-shrink-0" />
                <span className="text-[10px] font-mono font-semibold text-[#008069]">{`{{${ent.variableName || "?"}}}`}</span>
                {ent.prompt && (
                  <span className="text-[9px] text-gray-400 truncate ml-1">
                    {ent.prompt.length > 30 ? ent.prompt.substring(0, 30) + "..." : ent.prompt}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 italic">No entities configured</p>
        )}

        <div className="mt-1.5 text-[9px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block">
          {entities.length} entit{entities.length === 1 ? "y" : "ies"}
        </div>
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [entities, setEntities] = useState(
    data.entities ? data.entities.map((e) => ({ ...e })) : []
  );
  const promptRefs = useRef([]);

  const addEntity = () => {
    setEntities([
      ...entities,
      { id: `ent_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`, variableName: "", prompt: "" },
    ]);
  };

  const updateEntity = (idx, field, value) => {
    const updated = [...entities];
    updated[idx] = { ...updated[idx], [field]: value };
    setEntities(updated);
  };

  const removeEntity = (idx) => {
    setEntities(entities.filter((_, i) => i !== idx));
  };

  const insertVariableForEntity = (idx, varName) => {
    if (!varName) return;
    const current = entities[idx]?.prompt || "";
    const placeholder = `{{${varName}}}`;
    const textarea = promptRefs.current[idx];
    if (textarea) {
      const pos = textarea.selectionStart || current.length;
      const before = current.slice(0, pos);
      const after = current.slice(pos);
      updateEntity(idx, "prompt", before + placeholder + after);
    } else {
      updateEntity(idx, "prompt", current + placeholder);
    }
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#EDE9FE", borderRadius: "12px", padding: "14px", minWidth: "340px", maxWidth: "420px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-200 rounded-lg">
            <ListChecks size={14} className="text-indigo-700" />
          </div>
          <span className="text-[13px] font-bold text-indigo-900">AI Entity Extractor</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => onSave({ entities })}
            className="px-3 py-1.5 text-[11px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="aiEntityExtractor">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3 max-h-[400px] overflow-y-auto">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Entities to Extract</label>
            <button
              onClick={addEntity}
              className="text-[9px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5"
            >
              <Plus size={9} /> Add Entity
            </button>
          </div>

          {entities.length === 0 && (
            <p className="text-[9px] text-gray-400 italic">No entities. Click "Add Entity" to start extracting data.</p>
          )}

          {entities.map((ent, idx) => (
            <div key={ent.id} className="bg-gray-50 rounded-lg p-2.5 mb-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold text-gray-500 uppercase">Entity #{idx + 1}</span>
                <button onClick={() => removeEntity(idx)} className="p-0.5 text-red-400 hover:text-red-600">
                  <Trash2 size={10} />
                </button>
              </div>

              <div className="mb-2">
                <label className="flex items-center gap-1 text-[9px] font-bold text-gray-500 mb-0.5">
                  <Variable size={8} /> Save to variable
                </label>
                <VariableDropdown
                  value={ent.variableName}
                  onChange={(name) => updateEntity(idx, "variableName", name)}
                  flowVariables={flowVariables}
                  allowCreate
                  onCreateVariable={onCreateVariable}
                  placeholder="Select variable..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <label className="flex items-center gap-1 text-[9px] font-bold text-gray-500">
                    <Brain size={8} /> What to extract
                  </label>
                  <VariableDropdown
                    value=""
                    onChange={(name) => insertVariableForEntity(idx, name)}
                    flowVariables={flowVariables}
                    placeholder="Insert variable"
                    className="w-32"
                  />
                </div>
                <textarea
                  ref={(el) => {
                    promptRefs.current[idx] = el;
                  }}
                  value={ent.prompt}
                  onChange={(e) => updateEntity(idx, "prompt", e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 text-[10px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none placeholder:text-gray-400"
                  placeholder="Extract the order ID from the message"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onSave({ entities })}
          className="flex-1 py-2 text-[12px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main AIEntityExtractorNode ───────────────────────
const AIEntityExtractorNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("aiEntityExtractor");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-indigo-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      {/* Two source handles: Entity Found and Entity Not Found */}
      <Handle
        type="source"
        position={Position.Right}
        id="entity_found"
        className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-emerald-500"
        style={{ top: "33%", right: -7 }}
        title="Entity Found"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="entity_not_found"
        className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-red-400"
        style={{ top: "67%", right: -7 }}
        title="Entity Not Found"
      />

      {/* Labels for the two handles */}
      <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
        <span className="text-[7px] text-emerald-600 font-semibold text-center">Found</span>
        <span className="text-[7px] text-red-400 font-semibold text-center">Not Found</span>
      </div>
    </div>
  );
};

export default AIEntityExtractorNode;
